import { Outlet, useOutlet } from "react-router-dom";
import ModuleCard from "../../components/ModuleCard/ModuleCard";
import { useContext, useMemo } from "react";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { DssContext } from "../../shared/providers/dss/dss.provider";
import Link from "../../components/Link/Link";
import { routes } from "../../shared/router/routes";
import DssCard from "../../components/DssCard/DssCard";
import { DSS_SECTION } from "../../shared/constants";

function Home() {
    const outlet = useOutlet();
    const { translate } = useContext(TranslationContext);
    const { modules, pathwayModules } = useContext(ModuleContext);
    const { forms } = useContext(DssContext);
    const hasAssessment = useMemo(() => forms.some((f) => f.value), [forms]);
    const mentalForms = useMemo(
        () => forms.filter((form) => form.section === DSS_SECTION.MENTAL),
        [forms]
    );
    const physicalForms = useMemo(
        () => forms.filter((form) => form.section === DSS_SECTION.PHYSICAL),
        [forms]
    );

    const journeyModules = useMemo(
        () => modules?.filter((m) => m.moduleProgress && m.moduleProgress > 0) ?? [],
        [modules]
    );
    const suggestions = useMemo(() => pathwayModules?.slice(0, 5) ?? [], [pathwayModules]);

    return (
        <>
            {outlet && <Outlet />}
            {!outlet && (
                <div className="home px-1 py-22 px-md-31 pb-22 d-flex flex-column gap-3 gap-md-5">
                    {!hasAssessment && (
                        <section className="banner banner_dss d-flex flex-column gap-12 gap-md-3 align-items-md-start bg-neutrals-white">
                            <h2 className="text-h2-smbd">Decision Support System</h2>
                            <p className="description text-h3-rg mt-n03 mt-md-n21">
                                Get personalized recommendations and pathways for improving mental
                                and physical health in your workplace.
                            </p>
                            <Link
                                to={routes.DSS.path}
                                variant="solid"
                                customCss="text-center px-md-31">
                                {translate(
                                    "dss.start_assessment_workplace",
                                    "Start assessing your workplace"
                                )}
                            </Link>
                        </section>
                    )}

                    {!!journeyModules.length && (
                        <section aria-labelledby="topics" className="section_continue">
                            <h2 className="text-h2-smbd mb-12" id="topics">
                                {translate("common.continue_your_journey", "Continue your journey")}
                            </h2>
                            <div className="list">
                                {journeyModules.map((module) => (
                                    <ModuleCard
                                        key={module.id}
                                        {...module}
                                        title={module.title}
                                        contentAmount={module.contentAmount}
                                        description={module.description}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {!!suggestions.length && (
                        <section aria-labelledby="suggestions" className="section_suggestions">
                            <h2 className="text-h2-smbd mb-12" id="suggestions">
                                {translate("common.top_suggestions", "Top suggestions")}
                            </h2>
                            <div className="list">
                                {suggestions.map((module) => (
                                    <ModuleCard
                                        {...module}
                                        key={module.id}
                                        title={module.title}
                                        contentAmount={module.contentAmount}
                                        description={module.description}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {hasAssessment && (
                        <section className="d-flex flex-column gap-23 mb-23" aria-labelledby="dss">
                            <h2 className="text-h2-smbd" id="dss">
                                {translate(
                                    "dss.decision_support_system",
                                    "Decision Support System"
                                )}
                            </h2>
                            {!!mentalForms.length && (
                                <section aria-labelledby="title_mental_health">
                                    <h2
                                        id="title_mental_health"
                                        className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                                        <i
                                            className="icon icon-24 icon-neurology"
                                            aria-hidden="true"
                                        />
                                        {translate("dss.mental_health", "Mental health")}
                                    </h2>
                                    <div className="row g-12">
                                        {mentalForms.map((form, i) => (
                                            <div className="col-12 col-lg-4" key={i}>
                                                <DssCard form={form} onHome />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                            {!!physicalForms.length && (
                                <section aria-labelledby="title_physical_health">
                                    <h2
                                        id="title_physical_health"
                                        className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                                        <i
                                            className="icon icon-24 icon-neurology"
                                            aria-hidden="true"
                                        />
                                        {translate("dss.physical_health", "Physical health")}
                                    </h2>
                                    <div className="row g-12">
                                        {physicalForms.map((form, i) => (
                                            <div className="col-12 col-lg-4" key={i}>
                                                <DssCard form={form} onHome />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </section>
                    )}
                </div>
            )}
        </>
    );
}

export default Home;
